import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { FilterMatchMode } from 'primereact/api';
import { translatedMessage } from '../../service/LanguageService';
import GeneralUtils from '../../utilities/GeneralUtils';
import DataTableUtils from '../../utilities/DataTableUtils';
import EnumService from '../../service/EnumService';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessageDisplayComponent from '../../components/ErrorMessageDisplayComponent';
import { InstitutionService } from '../../service/InstitutionService';
import InstitutionDataTableUtils from '../../utilities/InstitutionDataTableUtils';
import { Button } from 'primereact/button';
import { hasPermission } from '../../service/UserService';
import { Toolbar } from 'primereact/toolbar';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';

const InstitutionList = () => {
    const [institutions, setInstitutions] = useState([]);
    const [institutionTypes, setInstitutionTypes] = useState([]);
    const [counties, setCounties] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [firstLoading, setFirstLoading] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('generic.error.get.data');
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);

    const toast = useRef(null);
    const dt = useRef(null);

    const institutionService = useMemo(() => new InstitutionService(), []);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: DataTableUtils.defalRowsPerPage(),
        page: 1,
        sortField: 'id',
        sortOrder: 1,
        filters: {
            name: { value: null, matchMode: FilterMatchMode.CONTAINS },
            type: { value: null, matchMode: FilterMatchMode.IN },
            status: { value: null, matchMode: FilterMatchMode.IN },
            county: { value: null, matchMode: FilterMatchMode.IN },
            locality: { value: null, matchMode: FilterMatchMode.CONTAINS },
            cif: { value: null, matchMode: FilterMatchMode.CONTAINS },
            secret: { value: null, matchMode: FilterMatchMode.EQUALS }
        }
    });
    const [lazyParamsUpdatedFromSession, setLazyParamsUpdatedFromSession] = useState(false);
    const propToColumnMap = {
        name: 'name',
        type: 'type_id',
        status: 'status',
        county: 'county_id',
        locality: 'locality',
        cif: 'cif'
    };
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        setFirstLoading(true);

        const setStaticValues = async () => {
            setInstitutionTypes(await institutionService.getNomInstitutionTypeByName({ name: null }));
            setCounties(await EnumService.getEntityLookupByField('NOM:COUNTY'));
        };

        setStaticValues();
        setFirstLoading(false);
    }, [institutionService]);

    useEffect(() => {
        loadInstitutions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyParams]);

    const setCriteria = () => {
        GeneralUtils.processSessionFilterValues('InstitutionsFilter', lazyParams, lazyParamsUpdatedFromSession, setLazyParamsUpdatedFromSession);

        let criteria = {};
        criteria.sortOrder = lazyParams.sortOrder < 0 ? 'DESC' : 'ASC';
        criteria.sortField = propToColumnMap[lazyParams.sortField] || 'name';
        criteria.name = lazyParams.filters.name.value;
        criteria.statuses = lazyParams.filters.status.value;
        criteria.typeIds = lazyParams.filters.type.value;
        criteria.countyIds = lazyParams.filters.county.value;
        criteria.locality = lazyParams.filters.locality.value;
        criteria.cif = lazyParams.filters.cif.value;
        criteria.secret = lazyParams.filters.secret.value;

        return criteria;
    };

    const loadInstitutions = async () => {
        setIsLoading(true);

        let criteria = setCriteria();
        criteria.startRow = lazyParams.first;
        criteria.pageSize = lazyParams.rows;

        await institutionService.getInstitutionsByCriteria(criteria)
            .then((data) => {
                // console.log(data.institutions);
                setInstitutions(data.institutions);
                setTotalRecords(data.totalCount);
                setIsLoading(false);
            })
            .catch((error) => {
                setError(true);
                setErrorMessage(error);
                // toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            });
    };

    const syncIntitutions = () => {
        setIsLoading(true);
        institutionService.syncInstitutions().then(
            () => {
                loadInstitutions();
                toast?.current?.show({ severity: 'success', summary: translatedMessage("institution.sync.success"), contentClassName: "pcn-toast-success" });
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
            }
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    {hasPermission('INSTITUTION_UPDATE') &&
                        <Button label={translatedMessage('institution.sync')}
                            icon="pi pi-sync"
                            className="p-button-primary mr-2"
                            onClick={() => syncIntitutions()}
                            disabled={isLoading}
                        />
                    }
                </div>
            </React.Fragment>
        );
    };

    // const handleExport = () => {
    //     let exportData = recruitmentCampaigns.map(item => ({ ...item }));
    //     exportData.map(item => {
    //         let exportItem = item
    //         exportItem.status = translatedMessage("RecruitmentStatus." + item.status)
    //         exportItem.lastUpdatedOn = new Date(Date.parse(item.lastUpdatedOn))
    //         delete exportItem.folderId
    //         delete exportItem.fileUuid
    //         delete exportItem.fileName
    //         delete exportItem.description

    //         return item;
    //     })

    //     return exportData
    // }

    const rightToolbarTemplate = () => {
        return (
            <></>
            // <ExportButtonComponent
            //     getExportData={handleExport}
            //     header={tableHeader}
            //     fileName={translatedMessage("recruitment.campaign")}                    
            // />
        );
    };

    const onPage = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.first = event.first;
        _lazyParams.page = event.page;
        _lazyParams.rows = event.rows;
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.sortField = event.sortField;
        _lazyParams.sortOrder = event.sortOrder;
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyParams(event);
    };


    if (firstLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else if (error) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ErrorMessageDisplayComponent message={errorMessage} />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <h3 className='mb-2'>{translatedMessage('institution.list.label')}</h3>

                            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                            <DataTable
                                ref={dt}
                                value={institutions}
                                dataKey="id"
                                paginator
                                lazy
                                totalRecords={totalRecords}
                                first={lazyParams.first}
                                rows={lazyParams.rows}
                                sortField={lazyParams.sortField}
                                sortOrder={lazyParams.sortOrder}
                                onPage={onPage}
                                onSort={onSort}
                                onFilter={onFilter}
                                rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                                className="datatable-responsive pcn-datatable"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                                emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                responsiveLayout="stack"
                                filterDisplay="row"
                                filters={lazyParams.filters}
                                loading={isLoading}
                            >
                                <Column 
                                    field="name" 
                                    header={translatedMessage('generic.name')} 
                                    sortable 
                                    headerStyle={{ width: '25%', minWidth: '10rem' }} 
                                    filter 
                                    showFilterMenu={false} 
                                />
                                <Column 
                                    field="cif" 
                                    header={translatedMessage('institution.cif')} 
                                    sortable 
                                    headerStyle={{ width: '10%', minWidth: '10rem' }} 
                                    filter 
                                    showFilterMenu={false} 
                                />
                                <Column
                                    field="type"
                                    header={translatedMessage('generic.type')}
                                    sortable
                                    body={(e) => e.type ? e.type.name : "-"}
                                    headerStyle={{ width: '15%', minWidth: '10rem' }}
                                    filter
                                    filterElement={(e) => InstitutionDataTableUtils.typeFilterTemplate(e, institutionTypes)}
                                    showFilterMenu={false}
                                />
                                <Column
                                    field="county"
                                    header={translatedMessage('generic.county')}
                                    sortable
                                    body={(e) => InstitutionDataTableUtils.countyBodyTemplate(e.county)}
                                    headerStyle={{ width: '10%', minWidth: '8rem' }}
                                    showFilterMenu={false}
                                    filter
                                    filterElement={(e) => InstitutionDataTableUtils.countyRowFilterTemplate(e, counties)}
                                />
                                <Column
                                    field="locality"
                                    header={translatedMessage('generic.locality')}
                                    sortable
                                    filter
                                    showFilterMenu={false}
                                    headerStyle={{ width: '10%', minWidth: '8rem' }}
                                />
                                <Column
                                    field="secret"
                                    header={translatedMessage('institution.secret')}
                                    sortable
                                    body={(e) => GeneralUtils.yesNoConverter(e.secret)}
                                    filter
                                    showFilterMenu={false}
                                    filterElement={(e) => DataTableUtils.yesNoFilterTemplate(e)}
                                    headerStyle={{ width: '7%', minWidth: '8rem' }}
                                />
                                <Column
                                    field="lastModifiedOn"
                                    header={translatedMessage('generic.lastUpdated.on')}
                                    sortable
                                    body={(e) => GeneralUtils.formatDate(e.lastModifiedOn)}
                                    headerStyle={{ width: '10%', minWidth: '8rem' }}
                                />
                            </DataTable>

                            {showValidationErrorDialog &&
                                <ValidationErrorModalComponent
                                    visible={showValidationErrorDialog}
                                    errors={validationErrors}
                                    dialogTitle={translatedMessage("recruitment.campaign.validation.title")}
                                    closeDialog={() => setShowValidationErrorDialog(false)}
                                />
                            }
                        </div>
                    </div>
                </div>
            </>
        );
    }
};

export default InstitutionList;
